/**
 * Navigation Item Component
 *
 * Single navigation item for sidebar.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib';
import type { NavItemProps } from '../../types';

export const NavItem = ({ item, isActive, isCollapsed, onClick }: NavItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Icon = item.icon;

  const handleClick = () => {
    if (item.path !== '#more') {
      navigate(item.path);
    }
    onClick?.();
  };

  const label = t(item.labelKey, item.label);

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        isActive
          ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100'
          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',
        isCollapsed && 'justify-center px-2'
      )}
      title={isCollapsed ? label : undefined}
    >
      <div
        className={cn(
          'flex-shrink-0 flex items-center justify-center',
          isCollapsed ? 'w-6 h-6' : 'w-5 h-5'
        )}
      >
        <Icon className="w-5 h-5" />
      </div>

      {!isCollapsed && (
        <span className="flex-1 text-left text-sm font-medium truncate">
          {label}
        </span>
      )}

      {!isCollapsed && item.badge !== undefined && item.badge > 0 && (
        <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-white">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </button>
  );
};

export default NavItem;
